import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Abrechnungsmodell } from '../../../domain/model/abrechnungsmodell.model';
import { AbstractForm } from '../abstract-form.model';
import { FormGroupConfig } from '../form-config.model';
import { ErzeugungsanlageConfig } from '../erzeugungsanlage.config';
import { Abrechnungsmodelltyp, AbrechnungsmodelltypItem } from '../../../domain/model/abrechnungsmodell-typ.enum';
import { DatenquelleSpitzLight, DatenquelleSpitzLightItem } from '../../../domain/model/datenquelle-sitz-light.enum';
import { MatValidators } from '../../../common/material/components/mat-form-field-required.directive';
import { PredicateValidator } from '../validator/predicate.validator';
import { Feld } from '../../../domain/model/feld.enum';
import { AnlagenschluesselValidator } from '../validator/anlagenschluessel.validator';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    selector: 'redispatch-abrechnung-sub-form',
    templateUrl: './abrechnung-sub-form.component.html',
    styleUrls: [
        './abrechnung-sub-form.component.scss',
    ],
    standalone: false,
})
export class AbrechnungSubFormComponent extends AbstractForm<Abrechnungsmodell> implements OnInit, OnDestroy {

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;

    @Input({ required: true }) disabled: boolean;
    @Input({ required: true }) readonly: boolean;

    public readonly AbrechnungsmodelltypItems = AbrechnungsmodelltypItem.getAll();
    public readonly DatenquelleSpitzLightItems = DatenquelleSpitzLightItem.getAll();
    public readonly Feld = Feld;

    private onDestroy$ = new Subject<void>();

    constructor() {
        super(inject(UntypedFormBuilder));
    }

    get formGroupConfig(): FormGroupConfig<Abrechnungsmodell> {
        return {
            abrechnungsmodell: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                MatValidators.required,
            ],
            datenquelleSpitzLightModell: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                PredicateValidator.conditionalValidator(() => this.isSpitzLight, Validators.required),
            ],
            ausgewaehlterWetterdienstleister: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                PredicateValidator.conditionalValidator(() => this.isWetterDl, Validators.required),
            ],
            zugeordneteReferenzanlage: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                [
                    PredicateValidator.conditionalValidator(() => this.isReferenzanlage, Validators.required),
                    Validators.maxLength(AnlagenschluesselValidator.MAX_LENGTH),
                ],
            ],
        };
    }

    get isSpitzLight(): boolean {
        return this.f.abrechnungsmodell.value == Abrechnungsmodelltyp.SPITZLIGHT;
    }

    get isWetterDl(): boolean {
        const value = this.f.datenquelleSpitzLightModell.value;
        return this.isSpitzLight && (value == DatenquelleSpitzLight.WETTER_DL_NETZBETREIBER || value == DatenquelleSpitzLight.WETTER_DL_ANLAGENBETREIBER);
    }

    get isReferenzanlage(): boolean {
        return this.isSpitzLight && this.f.datenquelleSpitzLightModell.value == DatenquelleSpitzLight.REFERENZANLAGE;
    }

    ngOnInit(): void {
        this.f.abrechnungsmodell.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(_value => {
                this.f.datenquelleSpitzLightModell.setValue(null);
                this.f.ausgewaehlterWetterdienstleister.setValue(null);
                this.f.zugeordneteReferenzanlage.setValue(null);
                this.f.datenquelleSpitzLightModell.updateValueAndValidity();
                this.f.ausgewaehlterWetterdienstleister.updateValueAndValidity();
                this.f.zugeordneteReferenzanlage.updateValueAndValidity();
            });
        this.f.datenquelleSpitzLightModell.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(_value => {
                this.f.ausgewaehlterWetterdienstleister.setValue(null);
                this.f.zugeordneteReferenzanlage.setValue(null);
                this.f.ausgewaehlterWetterdienstleister.updateValueAndValidity();
                this.f.zugeordneteReferenzanlage.updateValueAndValidity();
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    public sanitize(abrechnungsmodell: Abrechnungsmodell): Abrechnungsmodell {
        if (this.isSpitzLight) {
            if (this.isWetterDl) {
                abrechnungsmodell.zugeordneteReferenzanlage = null;
            } else {
                abrechnungsmodell.ausgewaehlterWetterdienstleister = null;
            }
        } else {
            abrechnungsmodell.datenquelleSpitzLightModell = null;
            abrechnungsmodell.ausgewaehlterWetterdienstleister = null;
            abrechnungsmodell.zugeordneteReferenzanlage = null;
        }
        return abrechnungsmodell;
    }

}
